import { PrivateEventEditForbiddenError } from '../../api/private-event-update';
import type { UpdatePrivateEventInput } from '../../api/private-event-update';
import { PrivateEventNotFoundError, PrivateEventValidationError } from '../../api/privateEvents';

import type { StepErrors } from './private-event-validation';

export const CHECK_FIELDS_MESSAGE = 'Check the highlighted fields and try again.';

const LOCATION_PREFIX = 'location.';

/** The server keys location errors `location.venueId`; the form keys the same
 *  fields `venueId`. */
function toFormKeys(fieldErrors: Record<string, string>): StepErrors {
  const out: StepErrors = {};
  for (const [key, message] of Object.entries(fieldErrors)) {
    out[key.startsWith(LOCATION_PREFIX) ? key.slice(LOCATION_PREFIX.length) : key] = message;
  }
  return out;
}

interface SaveArgs {
  patch: UpdatePrivateEventInput;
  mutate: (patch: UpdatePrivateEventInput) => Promise<unknown>;
  setFieldErrors: (errors: StepErrors) => void;
  /** The server says this viewer can no longer edit the event. */
  onDenied: () => void;
}

/** One message for each refusal the PATCH can give, so each reads differently. */
function presentFailure(err: unknown, args: SaveArgs): Error {
  if (err instanceof PrivateEventValidationError) {
    args.setFieldErrors(toFormKeys(err.fieldErrors));
    return new Error(CHECK_FIELDS_MESSAGE);
  }
  if (err instanceof PrivateEventEditForbiddenError) {
    if (Object.keys(err.fieldErrors).length > 0) {
      args.setFieldErrors(toFormKeys(err.fieldErrors));
      return new Error('You can’t move this event to that location. Choose another.');
    }
    args.onDenied();
    return new Error('Your changes were not saved — you no longer have access to edit this event.');
  }
  if (err instanceof PrivateEventNotFoundError) {
    return new Error('This private event no longer exists, or you can no longer see it.');
  }
  return err instanceof Error ? err : new Error('Could not save — try again.');
}

/** Sends the changed fields. Nothing changed means no request at all. Throws a
 *  readable error for the section to show. */
export async function saveSection(args: SaveArgs): Promise<void> {
  if (Object.keys(args.patch).length === 0) return;
  try {
    await args.mutate(args.patch);
    args.setFieldErrors({});
  } catch (err) {
    throw presentFailure(err, args);
  }
}
